import type IEntity from './IEntity';

export enum ETemplateCategory {
    ONBOARDING = 'ONBOARDING',
    JAHRESGESPRAECH = 'JAHRESGESPRAECH',
    CUSTOM = 'CUSTOM',
}

export interface IComponentValidationResult {
    isValid: boolean;
    blockNavigation?: boolean;
    message?: string;
}

export type TEmployeeTalkComponentType =
    | 'text'
    | 'textInput'
    | 'multilineInput'
    | 'select'
    | 'checkbox'
    | 'suggestion'
    | 'nameGender'
    | 'cvUpload'
    | 'skillExtraction'
    | 'onboardingProgress'
    | 'aiAssistant'
    | 'welcome'
    | 'superPowers'
    | 'certificateSelection'
    | 'roleSelection'
    | 'resultsOverview'
    | 'completion';
// Komponenten-Struktur für die Templates

export interface IEmployeeTalkTemplateColumn {
    id: string;
    components: IEmployeeTalkTemplateComponent[];
}

export interface IEmployeeTalkTemplateSection {
    id: string;
    title: string;
    columns: IEmployeeTalkTemplateColumn[];
    order: number;
}

export interface IEmployeeTalkTemplatePage {
    id: string;
    title: string;
    icon?: string; // MUI Icon Name
    sections: IEmployeeTalkTemplateSection[];
    order: number;
    aiAssistant?: IPageAIAssistantConfig;
    subPages?: IEmployeeTalkTemplatePage[];
}

export interface IPageAIAssistantConfig {
    enabled: boolean;
    agentText: string;
    instructions: string;
    capabilities: {
        chat: boolean;
        formHelp: boolean;
        contextualTips: boolean;
    };
    triggers: {
        onPageLoad: boolean;
        onFormError: boolean;
        onInactivity: number; // Sekunden, 0 = deaktiviert
    };
}

// Hauptinterface für das Template
export default interface IEmployeeTalkTemplate extends IEntity {
    id: number;
    title: string;
    description: string;
    templateVersion: string;
    templateCategory: ETemplateCategory;
    pages: IEmployeeTalkTemplatePage[];
    isActive: boolean;
}

export interface IEmployeeTalkTemplateComponent {
    id: string;
    type: TEmployeeTalkComponentType;
    label: string;
    description: string;
    required: boolean;
    order: number;
    properties: TEmployeeTalkComponentProperties;
    system?: boolean;
}

export interface ITextComponentProperties {
    text: string;
}

export interface ITextInputComponentProperties {
    placeholder: string;
    maxLength: number;
    defaultValue?: string;
}

export interface IMultilineInputComponentProperties {
    placeholder: string;
    rows: number;
    maxLength: number;
    defaultValue?: string;
}

export interface ISelectComponentProperties {
    options: string[];
    multiple: boolean;
    defaultValue?: string | string[];
}

export interface ICheckboxComponentProperties {
    options: string[];
    defaultSelected?: string[];
}

export interface ISuggestionComponentProperties {
    enabledEntities: {
        skills: boolean;
        certificates: boolean;
        roles: boolean;
    };
    requireJustification: boolean;
    formState?: {
        skills: { title: string; id?: number; level: number }[];
        certificates: { title: string; id?: number }[];
        roles: { title: string; id: number }[];
        justification: string;
    };
}

export interface INameGenderComponentProperties {
    showUpdateButton: boolean;
}

export interface ICVUploadComponentProperties {
    maxFileSize: number; // in MB
    allowedFormats: string[];
}

export interface ISkillExtractionComponentProperties {
    allowManualSkills: boolean;
    requireLevelRating: boolean;
    hideLabels?: boolean;
}

export interface IOnboardingProgressComponentProperties {
    showPercentage: boolean;
    showStepNames: boolean;
}

export interface IAIAssistantComponentProperties {
    assistantName: string;
    welcomeMessage: string;
    enableChat: boolean;
    enableFormHelp: boolean;
    position: 'fixed' | 'inline';
}

export interface IWelcomeComponentProperties {
    showProcessOverview: boolean;
    customWelcomeText?: string;
}

// biome-ignore lint/complexity/noBannedTypes: bis properties vorhanden sind ausgeblendet
export type ISuperPowersComponentProperties = {
    // Empty - no properties needed
    // TODO: Add company-level settings for max selection limit
};

export interface ICertificateSelectionComponentProperties {
    hideLabels?: boolean;
}

export interface IRoleSelectionComponentProperties {
    hideLabels?: boolean;
}

export interface IResultsOverviewComponentProperties {
    showStatistics?: boolean;
}

export interface ICompletionComponentProperties {
    showNextSteps?: boolean;
}

export type TEmployeeTalkComponentProperties =
    | ITextComponentProperties
    | ITextInputComponentProperties
    | IMultilineInputComponentProperties
    | ISelectComponentProperties
    | ICheckboxComponentProperties
    | ISuggestionComponentProperties
    | INameGenderComponentProperties
    | ICVUploadComponentProperties
    | ISkillExtractionComponentProperties
    | IOnboardingProgressComponentProperties
    | IAIAssistantComponentProperties
    | IWelcomeComponentProperties
    | ISuperPowersComponentProperties
    | ICertificateSelectionComponentProperties
    | IRoleSelectionComponentProperties
    | IResultsOverviewComponentProperties
    | ICompletionComponentProperties;
